import { useCallback, useEffect, useRef, useState, type FocusEvent } from 'react';

const KEY = 'tk-sidebar-pinned';

function read(): boolean {
  try {
    // 2026-11-02: closed by default; it stays open only when the person pinned it.
    return localStorage.getItem(KEY) === 'true';
  } catch {
    return false;
  }
}

/** Pinned (full width) or collapsed (icons only, opens on hover/focus). Remembered per browser. */
export function useSidebarPinned(): [boolean, () => void] {
  const [pinned, setPinned] = useState(read);
  const toggle = useCallback(() => {
    setPinned((current) => {
      const next = !current;
      try {
        localStorage.setItem(KEY, String(next));
      } catch {
        // Storage blocked: the choice lasts for this visit only.
      }
      return next;
    });
  }, []);
  return [pinned, toggle];
}

export const PEEK_OPEN_MS = 140;
export const PEEK_CLOSE_MS = 220;

/**
 * The collapsed sidebar "peeks" open: after a short hover (so passing the mouse over it never
 * flashes it open), at once on keyboard focus; it closes a moment after the mouse leaves, when
 * focus moves out, on Escape, or after choosing a page.
 */
export function useSidebarPeek() {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);
  const clear = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const later = useCallback(
    (value: boolean, ms: number) => {
      clear();
      timer.current = window.setTimeout(() => setOpen(value), ms);
    },
    [clear],
  );
  useEffect(() => clear, [clear]);
  const close = useCallback(() => {
    clear();
    setOpen(false);
  }, [clear]);
  return {
    open,
    close,
    handlers: {
      onMouseEnter: () => later(true, PEEK_OPEN_MS),
      onMouseLeave: () => later(false, PEEK_CLOSE_MS),
      onFocus: () => {
        clear();
        setOpen(true);
      },
      onBlur: (event: FocusEvent<HTMLElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) close();
      },
    },
  };
}
